import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const search = new URLSearchParams(window.location.search);
    const code = search.get("code");
    const oauthError = search.get("error");

    const hash = window.location.hash || "";
    const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
    const legacySessionId = hashParams.get("session_id");

    if (oauthError) {
      toast.error(`Google sign-in cancelled (${oauthError})`);
      navigate("/login", { replace: true });
      return;
    }

    // New direct Google OAuth flow: ?code=…
    if (code) {
      (async () => {
        try {
          const { data } = await api.post("/auth/google", {
            code,
            redirect_uri: window.location.origin + "/auth/callback",
          });
          setUser(data.user);
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/browse", { replace: true });
        } catch (e) {
          toast.error(apiErrorMessage(e));
          navigate("/login", { replace: true });
        }
      })();
      return;
    }

    // Legacy Emergent-managed flow: #session_id=… (kept for safety during transition)
    if (legacySessionId) {
      (async () => {
        try {
          const { data } = await api.post("/auth/session", { session_id: legacySessionId });
          setUser(data.user);
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/browse", { replace: true });
        } catch (e) {
          toast.error(apiErrorMessage(e));
          navigate("/login", { replace: true });
        }
      })();
      return;
    }

    navigate("/login", { replace: true });
  }, [navigate, setUser]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-amber-50">
      <div className="flex items-center gap-3 text-orange-500">
        <BookOpen className="animate-pulse" size={28} />
        <span className="font-display text-lg">Signing you in…</span>
      </div>
    </div>
  );
}
